/**
 * Asset Card component for displaying individual gold asset
 * Enhanced with financial metrics and profit/loss indicators
 */

import { formatVND } from '@/utils/currencyUtils'
import { formatLocalDate } from '@/utils/dateUtils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { 
  IconTrendingUp, 
  IconTrendingDown,
  IconEdit,
  IconTrash
} from '@tabler/icons-react'
import type { GoldAsset } from '@/types/asset'

interface AssetCardProps {
  asset: GoldAsset
  currentPrice?: number | undefined // Current sell price for this gold type
  onEdit?: (asset: GoldAsset) => void
  onDelete?: (asset: GoldAsset) => void
  onSell?: (asset: GoldAsset) => void
}

export function AssetCard({ asset, currentPrice, onEdit, onDelete, onSell }: AssetCardProps) {
  const statusColor = asset.status === 'holding' 
    ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400' 
    : 'bg-muted text-muted-foreground'
  const statusText = asset.status === 'holding' ? 'Đang nắm giữ' : 'Đã bán'

  // Calculate current value and profit/loss for holding assets
  const totalCost = asset.quantity * asset.purchasePrice
  const currentValue = asset.status === 'holding' && currentPrice
    ? asset.quantity * currentPrice
    : totalCost
  const profitLoss = currentValue - totalCost
  const profitLossPercent = totalCost > 0 ? (profitLoss / totalCost) * 100 : 0
  const hasProfitLoss = asset.status === 'holding' && currentPrice !== undefined

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 sm:pb-6">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold truncate">{asset.name || 'N/A'}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">{asset.goldTypeName || 'N/A'}</p>
          </div>
          <span className={cn('px-2 sm:px-3 py-1 rounded-full text-xs font-medium shrink-0', statusColor)}>
            {statusText}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Số lượng</p>
            <p className="text-base sm:text-lg font-semibold break-words">{asset.quantity} lượng</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Giá mua</p>
            <p className="text-base sm:text-lg font-semibold break-words overflow-hidden">{formatVND(asset.purchasePrice)}</p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Ngày mua</p>
            <p className="text-xs sm:text-sm break-words">
              {asset.purchaseDate ? formatLocalDate(asset.purchaseDate) : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Chi phí</p>
            <p className="text-base sm:text-lg font-semibold break-words overflow-hidden">{formatVND(totalCost)}</p>
          </div>
        </div>

        {hasProfitLoss && (
          <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg border bg-muted/50">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                <p className="text-xs sm:text-sm text-muted-foreground">Giá trị hiện tại</p>
                <p className="text-base sm:text-lg font-bold text-primary break-words overflow-hidden">{formatVND(currentValue)}</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t">
                <div className="flex items-center gap-2">
                  {profitLoss >= 0 ? (
                    <IconTrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400 shrink-0" stroke={1.5} />
                  ) : (
                    <IconTrendingDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400 shrink-0" stroke={1.5} />
                  )}
                  <p className="text-xs sm:text-sm text-muted-foreground">Lợi nhuận/Lỗ</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs font-semibold shrink-0',
                      profitLoss >= 0
                        ? 'bg-green-100 text-green-700 border-green-300 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40'
                        : 'bg-red-100 text-red-700 border-red-300 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/40'
                    )}
                  >
                    {profitLoss >= 0 ? '↑' : '↓'} {formatVND(Math.abs(profitLoss))}
                  </Badge>
                  <span
                    className={cn(
                      'text-xs sm:text-sm font-semibold shrink-0',
                      profitLoss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    ({profitLoss >= 0 ? '+' : ''}{profitLossPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!hasProfitLoss && asset.status === 'holding' && (
          <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg border bg-muted/50">
            <p className="text-xs sm:text-sm text-muted-foreground">
              Giá hiện tại chưa có sẵn
            </p>
          </div>
        )}

        {asset.notes && (
          <div className="mt-3 sm:mt-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-1">Ghi chú</p>
            <p className="text-xs sm:text-sm break-words">{asset.notes}</p>
          </div>
        )}
      </CardContent>
      {(onEdit || onDelete || onSell) && (
        <CardFooter className="flex gap-2 pt-0 px-4 sm:px-6 pb-4 sm:pb-6">
          {onEdit && (
            <Button
              onClick={() => onEdit(asset)}
              variant="outline"
              className="flex-1 min-h-[44px] text-xs sm:text-sm"
              size="sm"
            >
              <IconEdit className="h-4 w-4 sm:mr-2" stroke={1.5} />
              <span className="hidden sm:inline">Sửa</span>
            </Button>
          )}
          {onSell && asset.status === 'holding' && (
            <Button
              onClick={() => onSell(asset)}
              variant="default"
              className="flex-1 min-h-[44px] text-xs sm:text-sm"
              size="sm"
            >
              Bán
            </Button>
          )}
          {onDelete && (
            <Button
              onClick={() => onDelete(asset)}
              variant="destructive"
              className="flex-1 min-h-[44px] text-xs sm:text-sm"
              size="sm"
            >
              <IconTrash className="h-4 w-4 sm:mr-2" stroke={1.5} />
              <span className="hidden sm:inline">Xóa</span>
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  )
}

